import React, { useEffect, useMemo, useState } from 'react';
import GmModal from 'app/shared/modal/modal';
import { MultiSelect } from 'app/shared/select';
import { Button } from 'app/shared/button';
import { useIssueService } from 'hooks/api/agents/issue';
import { Board, BoardCard } from 'app/shared/board';
import { CreateIssueForm } from '../create';
import { IssueDetailModal } from '../detail';
import { SEVERITY_OPTIONS, CATEGORY_OPTIONS, labelFor } from '../constants';
import styles from '../issues.module.css';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';

const ISSUE_COLUMNS = [
  { key: 'open', label: 'Open', color: '#ef4444' },
  { key: 'investigating', label: 'Investigating', color: '#f59e0b' },
  { key: 'resolved', label: 'Resolved', color: '#10b981' },
  { key: 'closed', label: 'Closed', color: '#8e8e93' }
];

const SORT_OPTIONS = [
  { label: 'Recently active', value: '-last_reported_at' },
  { label: 'Most widespread', value: '-reports_count' }
];

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '';

export const IssuesBoard = () => {
  const { fetchIssues } = useIssueService();

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [severity, setSeverity] = useState(null);
  const [category, setCategory] = useState(null);
  const [sort, setSort] = useState(SORT_OPTIONS[0]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadIssues = () => {
    const parts = [`sort_by=${sort.value}`, 'population=200'];
    if (severity) parts.push(`severity=${severity.value}`);
    if (category) parts.push(`category=${category.value}`);
    setLoading(true);
    fetchIssues({ query_string: parts.join('&') })
      .then(({ issues: fetched }) => setIssues(fetched || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severity, category, sort]);

  const items = useMemo(
    () => issues.map((issue) => ({ ...issue, status: issue.status || 'open' })),
    [issues]
  );

  const applyIssueUpdate = (updated) => {
    const id = updated.id || updated._id;
    setIssues((prev) => prev.map((i) => ((i.id || i._id) === id ? { ...i, ...updated } : i)));
  };

  const renderCard = (issue) => (
    <BoardCard
      key={issue.id || issue._id}
      title={issue.title || '—'}
      subtitle={`${labelFor(SEVERITY_OPTIONS, issue.severity)} · ${labelFor(
        CATEGORY_OPTIONS,
        issue.category
      )}`}
      meta={`${issue.reports_count || 0} reports${
        issue.last_reported_at ? ` · ${formatDate(issue.last_reported_at)}` : ''
      }`}
      badge={issue.jira_key || ''}
      onClick={() => setSelected(issue)}
    />
  );

  return (
    <>
      <GridRow num_of_columns={5}>
        <GridColumn>
          <MultiSelect
            options={SEVERITY_OPTIONS}
            value={severity}
            onChange={setSeverity}
            isMulti={false}
            isClearable
            placeholder="All severities"
            menuPortalTarget={document.body}
          />
        </GridColumn>
        <GridColumn>
          <MultiSelect
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={setCategory}
            isMulti={false}
            isClearable
            placeholder="All categories"
            menuPortalTarget={document.body}
          />
        </GridColumn>
        <GridColumn>
          <MultiSelect
            options={SORT_OPTIONS}
            value={sort}
            onChange={(option) => setSort(option || SORT_OPTIONS[0])}
            isMulti={false}
            isClearable={false}
            menuPortalTarget={document.body}
          />
        </GridColumn>
        <GridColumn />
        <GridColumn>
          <Button icon_name="add" text="Create Issue" onClick={() => setShowCreate(true)} />
        </GridColumn>
      </GridRow>
      <Spacer multiple={4} />
      <Board
        columns={ISSUE_COLUMNS}
        items={items}
        group_by="status"
        loading={loading}
        empty_text="No issues"
        renderCard={renderCard}
      />

      <GmModal
        title="Create Issue"
        show_title
        show_modal={showCreate}
        onClose={() => setShowCreate(false)}
      >
        <CreateIssueForm onCreated={loadIssues} />
      </GmModal>

      <GmModal
        show_header={false}
        show_modal={!!selected}
        bodyClassName={styles.detailModal}
        onClose={() => setSelected(null)}
      >
        {selected && <IssueDetailModal issue={selected} onUpdated={applyIssueUpdate} />}
      </GmModal>
    </>
  );
};

export default IssuesBoard;
